import React, { useState, useRef } from 'react';
import Counter from '../../ui/counter/counter';
import Button from '../../ui/button/button';
import ToggleLabel from '../../ui/toggle/toggle';
import Icon from '../../ui/icon/icon';
import { useTranslation } from 'react-i18next';

import ReactCrop from 'react-image-crop';
import { makeAspectCrop, TO_RADIANS, centerAspectCrop, dataUrlToFileUsingFetch, useDebounceEffect } from './utils';

import 'react-image-crop/dist/ReactCrop.css';
import './cropImage.css';

let previewUrl = '';

function CropButtonPanel({
    imgSrc,
    scale = 1,
    onChangeScale = () => {},
    rotate = 0,
    onChangeRotate = () => {},
    aspect = null,
    onToggleAspect = () => {},
    forceRatio = false,
    valueWidth = 0,
    onChangeWidth = () => {},
    valueHeight = 0,
    onChangeHeight = () => {},
    labelRatio = 'Ratio',
    labelWidth = 'Width',
    labelHeight = 'Height',
    labelActions = 'Actions'
}) {
    const { t } = useTranslation();
    const _onChangeRotate = React.useCallback(
        value => {
            onChangeRotate(currentState => {
                let newRotate = currentState + value;
                if (Math.abs(newRotate) === 360) {
                    newRotate = 0;
                }
                return newRotate;
            });
        },
        [onChangeRotate]
    );

    const _onChangeScale = React.useCallback(
        value => {
            onChangeScale(currentState => {
                let newScale = Math.round((currentState + value) * 100) / 100;
                if (newScale < 0.1 || newScale > 3) {
                    return currentState;
                }
                return newScale;
            });
        },
        [onChangeScale]
    );

    const onReset = React.useCallback(() => {
        onChangeRotate(0);
        onChangeScale(1);
    }, [onChangeScale, onChangeRotate]);

    return (
        <div className='wz-crop-panel'>
            <div className='wz-crop-panel__container'>
                <div className='wz-crop-panel__container-left'>
                    <div className='wz-crop-panel__container-toggle-ratio'>
                        <div className='wz-crop-panel__toggle-ratio-label'>{labelRatio}</div>
                        <div className='wz-crop-panel__toggle'>
                            <ToggleLabel
                                onChange={!forceRatio ? onToggleAspect : undefined}
                                checked={Boolean(aspect)}
                                disabled={forceRatio}
                                className='wz-crop-panel__toggle-ratio'
                                aria-label={Boolean(aspect) ? t('widget.input.crop-modal.ratio-toggle-active') : t('widget.input.crop-modal.ratio-toggle-inactive')}
                            />
                        </div>
                    </div>
                    <div className='wz-crop-panel__container-size'>
                        <div className='wz-crop-panel__container-counter'>
                            <div>{labelWidth}</div>
                            <Counter placeholder={0} step={10} onChange={(_, value) => onChangeWidth(value)} value={valueWidth} min={0} />
                        </div>
                        <div className='wz-crop-panel__container-counter'>
                            <div>{labelHeight}</div>
                            <Counter placeholder={0} step={10} onChange={(_, value) => onChangeHeight(value)} value={valueHeight} min={0} />
                        </div>
                    </div>
                </div>
                <div className='wz-crop-panel__container-right'>
                    <div>
                        <div>{labelActions}</div>
                        <div className='wz-crop-panel__buttons'>
                            <Button
                                className='wz-crop-panel__button'
                                type='inverted'
                                disabled={!imgSrc || scale === 3}
                                onClick={() => _onChangeScale(0.05)}
                                label={<Icon name='zoomIn'/>}
                                aria-label={t('widget.input.crop-modal.zoomIn')}
                            />
                            <Button
                                className='wz-crop-panel__button'
                                type='inverted'
                                disabled={!imgSrc || scale === 0.1}
                                onClick={() => _onChangeScale(-0.05)}
                                label={<Icon name='zoomOut'/>}
                                aria-label={t('widget.input.crop-modal.zoomOut')}
                            />
                            <Button
                                className='wz-crop-panel__button'
                                type='inverted'
                                disabled={!imgSrc}
                                onClick={() => _onChangeRotate(-90)}
                                label={<Icon name='rotateLeft'/>}
                                aria-label={t('widget.input.crop-modal.rotateLeft')}
                            />
                            <Button
                                className='wz-crop-panel__button'
                                type='inverted'
                                disabled={!imgSrc}
                                onClick={() => _onChangeRotate(90)}
                                label={<Icon name='rotateRight'/>}
                                aria-label={t('widget.input.crop-modal.rotateRight')}
                            />
                            <Button
                                className='wz-crop-panel__button'
                                type='inverted' disabled={!imgSrc}
                                onClick={onReset}
                                label={<Icon name='reset'/>}
                                aria-label={t('widget.input.crop-modal.reset')}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function CropImage({
    imgSrc = null,
    imgTypeMime = 'image/jpeg',
    ratio = null,
    forceRatio = false,
    onChange = () => {},
    minWidth = 180,
    minHeight = 180,
    maxWidth = Infinity,
    maxHeight = Infinity,
    displayPanel = true,
    heightPreview = '400px',
    cropGrid = true,
    labelRatio = 'Ratio',
    labelWidth = 'Width',
    labelHeight = 'Height',
    labelActions = 'Actions',
    className = null,
    ...rest
}) {

    const imgRef = useRef(null);

    const [crop, setCrop] = useState(null);
    const [scale, setScale] = useState(1);
    const [rotate, setRotate] = useState(0);
    const [aspect, setAspect] = useState(ratio);

    //these states are the scale ratios in our original image and our crop canvas
    const [canvasScaleX, setCanvasScaleX] = useState(1);
    const [canvasScaleY, setCanvasScaleY] = useState(1);

    //When loading the source image and therefore also when changing the image,
    //this function allows you to position the crop, taking into account the ratio (aspect) and to center it if necessary
    const onImageLoad = React.useCallback(
        e => {
            const { width, height } = e.currentTarget;
            setCrop(centerAspectCrop(width, height, aspect));
            setTimeout(() => {
                let reactCropSelection = document.querySelector(".ReactCrop__crop-selection");
                let reactCropHandle = document.querySelectorAll(".ReactCrop__drag-handle");
                reactCropHandle.forEach(elem => {
                    elem.setAttribute('tabindex', '-1');
                });
                reactCropSelection.setAttribute('tabindex', '-1');
            }, 100);
        },
        [aspect]
    );

    const onToggleAspect = React.useCallback(() => {
        let ratio = undefined;
        if (!aspect && imgRef.current) {
            const { width, height } = imgRef.current;
            ratio = crop.width / crop.height;
            setCrop(makeAspectCrop(crop, ratio, width, height));
        }
        setAspect(ratio);
    }, [crop, imgRef, aspect]);

    const onChangeWidth = React.useCallback(
        value => {
            const { width, height } = imgRef.current;
            setCrop(currentState => {
                let newCrop = { ...currentState, width: value / canvasScaleX };
                if (aspect) {
                    delete newCrop.height;
                }
                return makeAspectCrop(newCrop, aspect, width, height);
            });
        },
        [canvasScaleX, imgRef, aspect]
    );

    const onChangeHeight = React.useCallback(
        value => {
            const { width, height } = imgRef.current;
            setCrop(currentState => {
                let newCrop = { ...currentState, height: value / canvasScaleY };
                if (aspect) {
                    delete newCrop.width;
                }
                return makeAspectCrop(newCrop, aspect, width, height);
            });
        },
        [canvasScaleY, imgRef, aspect]
    );

    const minWidthCrop = React.useMemo(() => {
        return minWidth / canvasScaleX;
    }, [canvasScaleX, minWidth]);

    const minHeightCrop = React.useMemo(() => {
        return minHeight / canvasScaleY;
    }, [canvasScaleY, minHeight]);

    const maxWidthCrop = React.useMemo(() => {
        return maxWidth / canvasScaleX;
    }, [canvasScaleX, minWidth]);

    const maxHeightCrop = React.useMemo(() => {
        return maxHeight / canvasScaleY;
    }, [canvasScaleY, minHeight]);

    //This function creates the canvas, draws the crop output image and generates a blobUrl
    function getCroppedImage(image, crop, fileName, scale, rotate, typeMime) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('No 2d context');
        }

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        // devicePixelRatio slightly increases sharpness on retina devices
        // at the expense of slightly slower render times and needing to
        // size the image back down if you want to download/upload and be
        // true to the images natural size.
        // const pixelRatio = window.devicePixelRatio;
        const pixelRatio = 1;
        setCanvasScaleX(scaleX);
        setCanvasScaleY(scaleY);

        canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
        canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

        ctx.scale(pixelRatio, pixelRatio);
        ctx.imageSmoothingQuality = 'high';

        const cropX = crop.x * scaleX;
        const cropY = crop.y * scaleY;

        const rotateRads = rotate * TO_RADIANS;
        const centerX = image.naturalWidth / 2;
        const centerY = image.naturalHeight / 2;

        ctx.save();

        // Move the crop origin to the canvas origin (0,0)
        ctx.translate(-cropX, -cropY);
        // Move the origin to the center of the original position
        ctx.translate(centerX, centerY);
        // Rotate around the origin
        ctx.rotate(rotateRads);
        // Scale the image
        ctx.scale(scale, scale);
        // Move the center of the image to the origin (0,0)
        ctx.translate(-centerX, -centerY);
        ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, image.naturalWidth, image.naturalHeight);

        return new Promise((resolve, reject) => {
            canvas.toBlob(
                blob => {
                    if (!blob) {
                        console.error('Canvas is empty');
                        return;
                    }
                    blob.name = fileName;
                    if (previewUrl) {
                        URL.revokeObjectURL(previewUrl);
                    }
                    previewUrl = URL.createObjectURL(blob);
                    resolve(previewUrl);
                },
                typeMime,
                0.9
            );
        });
    }

    //This function takes care of drawing the image at the output of the crop,
    //generating a blobUrl and a File object which are automatically passed to the onChange
    async function makeClientCrop(crop, onChange, imgRef, scale, rotate, typeMime) {
        if (imgRef && crop.width && crop.height) {
            let ouputExtension = typeMime === 'image/png' ? '.png' : '.jpeg';
            let outputTypeMime = typeMime === 'image/png' ? 'image/png' : 'image/jpeg';
            const _croppedImageUrl = await getCroppedImage(imgRef.current, crop, `newFile${ouputExtension}`, scale, rotate, outputTypeMime);
            const _file = await dataUrlToFileUsingFetch(_croppedImageUrl, `newFile${ouputExtension}`, outputTypeMime);
            onChange(_croppedImageUrl, _file);
        }
    }

    //The logic is based on triggering this useEffect by modifying the crop, rotate and scale states
    useDebounceEffect(
        async () => {
            if (crop?.width && crop?.height && imgRef.current) {
                makeClientCrop(crop, onChange, imgRef, scale, rotate, imgTypeMime);
            }
        },
        100,
        [scale, rotate, crop, onChange, imgRef, imgTypeMime]
    );

    return (
        <div className={`wz-crop-image__container ${className}`}>
            <div className="wz-crop-modal-anchor"></div>
            {!!imgSrc && (
                <div className='wz-crop-image'>
                    <div className='wz-crop-image__border'>
                        <ReactCrop
                            crop={crop}
                            onChange={(crop, _) => setCrop(crop)}
                            aspect={aspect}
                            minWidth={minWidthCrop}
                            minHeight={minHeightCrop}
                            maxWidth={maxWidthCrop}
                            maxHeight={maxHeightCrop}
                            ruleOfThirds={cropGrid}
                            {...rest}
                        >
                            <img
                                ref={imgRef}
                                className='wz-crop-image__preview'
                                alt="crop"
                                src={imgSrc}
                                style={{ maxHeight: `${heightPreview}`, transform: `scale(${scale}) rotate(${rotate}deg)` }}
                                onLoad={onImageLoad}
                            />
                        </ReactCrop>
                    </div>
                </div>
            )}
            {displayPanel && (
                <CropButtonPanel
                    imgSrc={imgSrc}
                    scale={scale}
                    onChangeScale={setScale}
                    rotate={rotate}
                    onChangeRotate={setRotate}
                    aspect={aspect}
                    labelRatio={labelRatio}
                    onToggleAspect={onToggleAspect}
                    forceRatio={forceRatio}
                    labelWidth={labelWidth}
                    valueWidth={crop ? crop.width * canvasScaleX : 0}
                    onChangeWidth={onChangeWidth}
                    labelHeight={labelHeight}
                    valueHeight={crop ? crop.height * canvasScaleY : 0}
                    onChangeHeight={onChangeHeight}
                    labelActions={labelActions}
                />
            )}
        </div>
    );
}
